import React, { useState, useEffect, useMemo, useRef } from 'react';

/**
 * Search-by-title combobox + list to choose a passage id.
 *
 * @param {object} props
 * @param {string} props.inputId - id for the text input (label htmlFor)
 * @param {React.ReactNode} props.label - label content
 * @param {Array<{ passage_id: number|string, title?: string }>} props.passages
 * @param {boolean} props.passagesLoading
 * @param {string} props.passagesError
 * @param {string|number} props.value - selected passage id; '' or null = none
 * @param {(nextId: string) => void} props.onChange - '' when cleared
 * @param {boolean} [props.extraDisabled] - e.g. parent loading passage detail
 * @param {string} [props.titleFallback] - title when id selected but row missing from passages
 * @param {string} [props.placeholder]
 * @param {boolean} [props.hiddenRequired] - hidden required input for native form validation
 * @param {React.ReactNode} [props.children] - after list hints (e.g. passage load status)
 */
function PassagePicker({
  inputId,
  label,
  passages,
  passagesLoading,
  passagesError,
  value,
  onChange,
  extraDisabled = false,
  titleFallback = '',
  placeholder = 'Search by title, then pick a row…',
  hiddenRequired = false,
  children,
}) {
  const [passageSearch, setPassageSearch] = useState('');
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const valueStr = value === '' || value == null ? '' : String(value);

  useEffect(() => {
    if (valueStr === '') setPassageSearch('');
  }, [valueStr]);

  const filteredPassages = useMemo(() => {
    const q = passageSearch.trim().toLowerCase();
    let list = !q
      ? passages
      : passages.filter((p) => (p.title || '').toLowerCase().includes(q));
    if (valueStr !== '') {
      const id = Number(valueStr);
      const inList = list.some((p) => Number(p.passage_id) === id);
      if (!inList) {
        const sel = passages.find((p) => Number(p.passage_id) === id);
        if (sel) list = [sel, ...list];
      }
    }
    return list;
  }, [passages, passageSearch, valueStr]);

  const selectedTitle = useMemo(() => {
    if (valueStr === '') return '';
    const id = Number(valueStr);
    const row = passages.find((p) => Number(p.passage_id) === id);
    const fromList = row?.title != null ? String(row.title) : '';
    if (fromList) return fromList;
    const t = titleFallback;
    return t != null ? String(t) : '';
  }, [valueStr, passages, titleFallback]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        setFocused(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const inputDisplay = focused ? passageSearch : valueStr !== '' ? selectedTitle : passageSearch;

  const pickerDisabled =
    passagesLoading ||
    !!passagesError ||
    extraDisabled ||
    (!passagesLoading && passages.length === 0);

  const listOpen =
    focused && !pickerDisabled && passages.length > 0 && !passagesError;

  const listboxId = `${inputId}-listbox`;

  return (
    <>
      {hiddenRequired ? (
        <input type="hidden" value={valueStr} required readOnly aria-hidden tabIndex={-1} />
      ) : null}
      <label htmlFor={inputId}>{label}</label>
      <div className="passage-picker" ref={wrapRef}>
        <div className="passage-picker-row">
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            value={inputDisplay}
            onChange={(e) => setPassageSearch(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            autoComplete="off"
            placeholder={placeholder}
            disabled={pickerDisabled}
            aria-expanded={listOpen}
            aria-controls={listboxId}
            aria-autocomplete="list"
            role="combobox"
          />
          <button
            type="button"
            className="passage-picker-clear"
            disabled={pickerDisabled}
            onClick={() => {
              onChange('');
              inputRef.current?.blur();
            }}
          >
            Clear
          </button>
        </div>
        {listOpen ? (
          <ul id={listboxId} className="passage-picker-panel" role="listbox">
            {filteredPassages.map((p) => {
              const active = valueStr !== '' && Number(valueStr) === Number(p.passage_id);
              return (
                <li
                  key={p.passage_id}
                  role="option"
                  aria-selected={active}
                  className={active ? 'is-active' : undefined}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(String(p.passage_id));
                    setFocused(false);
                    inputRef.current?.blur();
                  }}
                >
                  {p.title}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      {passagesLoading ? (
        <div className="loading-state" style={{ marginTop: 8 }}>
          Loading passage list…
        </div>
      ) : null}
      {passagesError ? (
        <div className="message error" style={{ marginTop: 8 }}>
          {passagesError}
        </div>
      ) : null}
      {!passagesLoading && !passagesError && passages.length === 0 ? (
        <small className="form-hint" style={{ display: 'block', marginTop: 8 }}>
          No passages in the database yet.
        </small>
      ) : null}
      {children}
    </>
  );
}

export default PassagePicker;
